<template>
  <div>
    <ShareModal/>
    <add-to-playlist-modal
      ref="modal"
      :mixVersion="modal.version"
      :mixID="modal.mixID"
      :songID="modal.songID"
      :songName="modal.songName"
      :playlists="modal.playlists"
      :show="modal.show"
    />
    <draggable v-model="items" :handle="dragHandleClass" @end="reorderPlaylist" itemKey="id">
      <mix-player
        v-for="item in items"
        :key="item.id"
        :mix="item.mix"
        :isOpen="item.isOpen"
        :canAB="item.canAB"
        :playlistID="playlist && playlist.id"
        :canEditPlaylist="playlist && playlist.can_edit_playlist"
        :ref="`player_${item.id}`"
        @add-to-playlist="openAddToPlaylist(item.mix)"
        @remove-from-playlist="removeFromPlaylist(item.id)"
        @update-state="updateMixState(item.id, $event)"
        @update-time="updatePlayerTime(item.id, $event)"
        />
    </draggable>
  </div>
</template>

<script>
import MixPlayer from './mix_player.vue'
import { VueDraggableNext } from 'vue-draggable-next'

import bus from "../bus.js"
import MixAlign from "./align.js"
import AddToPlaylistModal from "./add_to_playlist_modal.vue"
import ShareModal from "./share_modal.vue"

import { post_csrf } from '../utils/csrf'

export default {
  components: { AddToPlaylistModal, MixPlayer, draggable: VueDraggableNext, ShareModal },
  props: ["mixes", "playlist"],
  data() {
    return {
      modal: {
        version: undefined,
        mixID: undefined,
        songID: undefined,
        songName: undefined,
        playlists: [],
        show: false
      },
      items: this.buildItems(),
      activePlayerIndex: 0
    }
  },
  computed: {
    activePlayer() {
      return this.playerRefForID(this.items[this.activePlayerIndex].id)
    },
    dragHandleClass() {
      if ( this.playlist )
        return ".handle"
      else
        return ".not-a-class-nope"
    },
  },
  watch: {
    playlist() { this.items = this.buildItems() },
    mixes() { this.items = this.buildItems() },
  },
  methods: {
    buildItems() {
      let items

      if ( this.playlist ) {
        items = this.playlist?.items?.map(item => ({...item})) || []
      } else {
        items = this.mixes.map(mix => ({...mix, mix: mix}))
      }

      let isFirst = true
      items.forEach(i => {
        i.state = 'pause'
        i.canAB = false
        if ( this.playlist ) {
          i.isOpen = false
        } else {
          i.isOpen = isFirst
          isFirst = false
        }
      })

      return items
    },
    playerRefForID(id) {
      const arr = this.$refs[`player_${id}`]
      if ( arr )
        return arr[0]
    },
    async removeFromPlaylist(id) {
      const idx = this.findIndexForID(id)
      if ( !this.playlist )
        return

      const url = `/playlists/${this.playlist.id}/tracks/${id}`
      const res = await post_csrf(url, {}, { method: "DELETE" } )
      if ( res.status == 200 ) {
        if ( this.activePlayerIndex == idx ) {
          this.activePlayer.pause()
          this.activePlayerIndex = 0
        }

        this.items.splice(idx, 1)
      } else {
        alert("error deleting item from playlist!")
      }
    },
    openAddToPlaylist(mix) {
      this.modal.version = mix.version
      this.modal.mixID = mix.id
      this.modal.songName = mix.song_name
      this.modal.songID = mix.song_id
      this.$refs.modal.showModal(mix.id)
    },
    async reorderPlaylist(ev) {
      if ( !this.playlist ) {
        return
      }

      if ( this.activePlayerIndex == ev.oldIndex ) {
        this.activePlayerIndex = ev.newIndex
      } else if ( this.activePlayerIndex == ev.newIndex ) {
        this.activePlayerIndex = ev.oldIndex
      }

      const playlist_item_ids = this.items.map(item => item.id)

      const url = `/playlists/${this.playlist.id}/reorder`
      const res = await post_csrf(url, { playlist_item_ids })

      if ( res.status !== 200 ) {
        alert("Error reordering playlist!")
      }
    },
    findIndexForID(id) {
      const idx = this.items.findIndex(i => i.id == id)
      if ( idx === undefined ) {
        throw new Error("couldn't find matching item for item id: " + id)
      }
      return idx
    },
    async crossFade(oldPlayer, oldVol, newPlayer, newVol) {
      let intervals = 10
      let oldStep = oldVol / intervals
      let newStep = newVol / intervals

      for (let i = 0; i < intervals; i++) {
        oldPlayer.setVolume(oldVol - (oldStep * i))
        newPlayer.setVolume(newVol - ((10 - i) * newStep))

        await new Promise(resolve => setTimeout(resolve, 20))
      }
    },
    async updateMixState(id, playState ) {
      const idx = this.findIndexForID(id)
      this.items[idx].state = playState

      if ( playState == 'begin-playback' ) {
        /* expand the mix about to start playing, pause/close anything else playing */
        for ( let i = 0; i < this.items.length; i++ ) {
          if ( i == idx ) {
            this.items[i].isOpen = true
          } else {
            this.items[i].isOpen = false
            const ref = this.playerRefForID(this.items[i].id)
            ref && ref.pause()
          }
        }
      }

      if ( playState == 'begin-ab' ) {
        this.items[idx].isOpen = true
        this.items[idx].state = playState

        const oldPlayer = this.activePlayer
        const newPlayer = this.playerRefForID(id)
        await newPlayer.initWS()
        newPlayer.initWSAudio()

        let mix_align = new MixAlign(oldPlayer, newPlayer)

        let oldVol = oldPlayer.getVolume()
        let newVol = newPlayer.getVolume()

        await mix_align.align()
        await this.crossFade(oldPlayer, oldVol, newPlayer, newVol)

        oldPlayer.pause()
        oldPlayer.setVolume(oldVol)
        newPlayer.endAB()

        this.items[this.activePlayerIndex].state = 'pause'
        this.items[idx].state = 'play'
      }

      bus.emit("player-state-change", playState)
      if ( playState != 'pause' ) {
        this.activePlayerIndex = idx
      }
      this.setCanAB()
    },
    setCanAB() {
      if ( this.items.find(i => i.state == 'play') ) {
        this.items.forEach(i => i.canAB = (i.state == 'pause'))
      } else {
        this.items.forEach(i => i.canAB = false)
      }
    },
    updatePlayerTime(id, payload) {
      const idx = this.findIndexForID(id)
      // avoid emitting player-time-update events when seeking in inactive mixes
      if ( idx != this.activePlayerIndex )
        return

      bus.emit("player-time-update", { mix: this.items[idx].mix, time: payload.time, duration: payload.duration })
    },
    onKeyDown(e) {
      var tag = e.target.tagName.toLowerCase();
      if ( tag != 'input' && tag != 'textarea' ) {
        if ( e.which === 32 ) {
          e.preventDefault()
          this.activePlayer && this.activePlayer.playPause()
        } else if ( e.which == 37 ) {
          e.preventDefault()
          this.activePlayer && this.activePlayer.back15()
        } else if ( e.which == 39 ) {
          e.preventDefault()
          this.activePlayer && this.activePlayer.forward15()
        }
      }
    },
  },
  mounted() {
    bus.on("player-control", control => {
      if ( control == "pause" ) {
        this.activePlayer && this.activePlayer.pause()
      } else if ( control == "play" ) {
        if ( !this.activePlayer ) {
          this.activePlayerIndex = 0
        }
        this.activePlayer && this.activePlayer.play()
      } else if ( control == "previous" ) {
        this.activePlayer && this.activePlayer.pause()
        this.activePlayerIndex--

        if ( this.activePlayerIndex < 0 )
          this.activePlayerIndex = this.items.length - 1

        this.activePlayer.play()
      } else if ( control == "next" ) {
        this.activePlayer && this.activePlayer.pause()
        this.activePlayerIndex++

        this.activePlayerIndex = this.activePlayerIndex % this.items.length

        this.activePlayer.play()
      } else if ( control == "forward15" ) {
        this.activePlayer && this.activePlayer.forward15()
      } else if ( control == "back15" ) {
        this.activePlayer && this.activePlayer.back15()
      }
    }),
    document.addEventListener('keydown', this.onKeyDown)
  },
  beforeDestroy() {
    bus.off("player-control")
    document.removeEventListener('keydown', this.onKeyDown)
  }
}
</script>
